import React, { useState } from "react";

const FIXED_FALSE_FIELDS = new Set(["export_raw_logits"]);

export function parseFaceDetectionConfig(value) {
    const config = typeof value === "string" ? JSON.parse(value) : value;
    if (!config || typeof config !== "object" || Array.isArray(config)) {
        throw new Error("train_cfg must be a JSON object");
    }
    return config;
}

export function faceDetectionConfigFields(config) {
    return Object.fromEntries(Object.entries(config).map(([key, value]) => [
        key, typeof value === "object" ? JSON.stringify(value) : String(value),
    ]));
}

function fieldType(key, config, types) {
    return types[key] || (typeof config[key] === "number" ? "float"
        : typeof config[key] === "boolean" ? "bool"
        : typeof config[key] === "object" ? "json.loads" : "str");
}

export function serializeFaceDetectionConfig(fields, config, types) {
    const result = {};
    for (const [key, value] of Object.entries(fields)) {
        const kind = fieldType(key, config, types);
        if (kind === "int" || kind === "float") {
            const number = Number(value);
            if (!String(value).trim() || !Number.isFinite(number) ||
                (kind === "int" && !Number.isInteger(number))) {
                throw new Error(`${key} must be a finite ${kind}`);
            }
            result[key] = number;
        } else if (kind === "bool") {
            if (value !== "true" && value !== "false") {
                throw new Error(`${key} must be true or false`);
            }
            result[key] = value === "true";
        } else if (kind === "json.loads") {
            try {
                result[key] = JSON.parse(value);
            } catch {
                throw new Error(`${key} must be valid JSON`);
            }
        } else {
            result[key] = value;
        }
    }
    if (result.num_gpu !== undefined && (!Number.isInteger(result.num_gpu) || result.num_gpu < 1)) {
        throw new Error("num_gpu must be a positive integer");
    }
    if (result.num_gpu !== undefined && result.batch_size !== undefined && result.batch_size % result.num_gpu !== 0) {
        throw new Error("batch_size must be divisible by num_gpu");
    }
    return JSON.stringify(result, null, 2);
}

export default function FaceDetectionParameters({ config, fields, types, requiredFields, onChange, onRemove }) {
    const [newParameter, setNewParameter] = useState("");
    const available = Object.keys(types).filter(key => !(key in fields));
    return (
        <>
            {Object.entries(fields).map(([key, value]) => {
                const kind = fieldType(key, config, types);
                const id = `facedetection-config-${key}`;
                return (
                    <div className="model-info-row" key={key}>
                        <label htmlFor={id}>{key}:</label>
                        {kind === "bool" ? (
                            <select id={id} value={value} className="model-info-input"
                                disabled={FIXED_FALSE_FIELDS.has(key)}
                                onChange={event => onChange(key, event.target.value)}>
                                <option value="true">true</option>
                                <option value="false">false</option>
                            </select>
                        ) : (
                            <input id={id} type={kind === "int" || kind === "float" ? "number" : "text"}
                                step={kind === "int" ? "1" : "any"}
                                min={key === "num_gpu" ? 1 : undefined}
                                value={value} className="model-info-input"
                                onChange={event => onChange(key, event.target.value)} />
                        )}
                        {!requiredFields.includes(key) && (
                            <button type="button" className="btn btn-outline-secondary btn-sm"
                                aria-label={`Remove ${key}`} onClick={() => onRemove(key)}>
                                Remove
                            </button>
                        )}
                    </div>
                );
            })}
            {available.length > 0 && (
                <div className="model-info-row">
                    <label htmlFor="facedetection-add-parameter">Add parameter:</label>
                    <select id="facedetection-add-parameter" className="model-info-input"
                        value={newParameter} onChange={event => setNewParameter(event.target.value)}>
                        <option value="">Select a parameter</option>
                        {available.map(key => <option key={key} value={key}>{key}</option>)}
                    </select>
                    <button type="button" className="btn btn-outline-secondary btn-sm"
                        disabled={!newParameter} onClick={() => {
                            onChange(newParameter, types[newParameter] === "bool" ? "false" : "");
                            setNewParameter("");
                        }}>
                        Add
                    </button>
                </div>
            )}
        </>
    );
}
